import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Trash2, Plus } from 'lucide-react';
import classNames from 'classnames';

import styles from './styles.module.css';
import ClientIcon from '@/svg-icons/client';

const BrySignersField = ({ formData, onChange, errorSchema = [] }) => {
  const intl = useIntl();
  const initial = (formData && formData.length ? formData : [{ name: '', email: '', status: undefined }]).map(s => ({
    ...s,
  }));
  const [signers, setSigners] = useState(initial);

  // Sync signers pre-filled asynchronously (e.g. derived from a finished order)
  useEffect(() => {
    if (Array.isArray(formData) && formData.length > 0 && JSON.stringify(formData) !== JSON.stringify(signers)) {
      setSigners(formData.map(s => ({ ...s })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const update = next => {
    setSigners(next);
    onChange(next);
  };

  const handleField = (index, key) => e => {
    const next = [...signers];
    next[index][key] = e.target.value;
    if (!next[index].id && !next[index].status) next[index].status = 'PENDING';
    update(next);
  };

  const add = () => {
    update([...signers, { name: '', email: '', status: undefined }]);
  };

  const remove = index => () => {
    const next = [...signers];
    const current = next[index];

    if (current?.id) {
      if (current.status !== 'PENDING') {
        return;
      }
      next.splice(index, 1);
    } else {
      next.splice(index, 1);
    }

    if (next.length === 0) {
      next.push({ name: '', email: '', status: undefined });
    }

    update(next);
  };

  const isErrorName = idx => !!errorSchema[idx] && signers[idx]?.name === '';
  const isErrorEmail = idx => !!errorSchema[idx] && !emailRegex.test(String(signers[idx]?.email || ''));

  const isLocked = signer => signer.status && signer.status !== 'PENDING';

  return (
    <div className={styles.rootContainer}>
      <div className={styles.sectionHeader}>
        <ClientIcon viewBox="0 0 34.016 34.016" className="w-6 h-6" />
        <span className={styles.sectionTitle}>
          <FormattedMessage id="signatures.title" />
        </span>
      </div>
      <div className={styles.table}>
        <div className={styles.tableHeader}>
          <span className={styles.headerCell}>
            <FormattedMessage id="fullName" />
          </span>
          <span className={styles.headerCell}>
            <FormattedMessage id="EMAIL" />
          </span>
          <span className={styles.headerCell} />
        </div>
        <div className={styles.tableBody}>
          {signers.map((signer, index) => {
            const showRemoveButton = signers.length > 1;

            return (
              <div className={styles.tableRow} key={index}>
                <div className={styles.fieldColumn}>
                  <div className="w-full">
                    <input
                      className={isErrorName(index) ? styles.bootstrapInputError : styles.bootstrapInput}
                      type="text"
                      value={signer.name || ''}
                      onChange={handleField(index, 'name')}
                      disabled={isLocked(signer)}
                      data-cy={`${index}-bry-signer-name`}
                      placeholder={intl.formatMessage({ id: 'recipients.Signer.name' })}
                    />
                    {isErrorName(index) ? (
                      <div className={styles.error}>
                        <ul>
                          <li>
                            <FormattedMessage id="requiredField" />
                          </li>
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className={styles.fieldColumn}>
                  <div className="w-full">
                    <input
                      className={isErrorEmail(index) ? styles.bootstrapInputError : styles.bootstrapInput}
                      type="email"
                      value={signer.email || ''}
                      onChange={handleField(index, 'email')}
                      disabled={isLocked(signer)}
                      data-cy={`${index}-bry-signer-email`}
                      placeholder={intl.formatMessage({ id: 'recipients.email' })}
                    />
                    {isErrorEmail(index) ? (
                      <div className={styles.error}>
                        <ul>
                          <li>
                            <FormattedMessage id="requiredEmail" />
                          </li>
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className={styles.actionsColumn}>
                  {showRemoveButton ? (
                    <button
                      disabled={isLocked(signer)}
                      className={classNames(styles.removeButton, 'rounded-full flex items-center justify-center', {
                        [styles.removeButtonDisabled]: isLocked(signer),
                      })}
                      onClick={remove(index)}
                      data-cy={`${index}-bry-signer-remove`}
                      type="button"
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                  {signer.status && signer.status !== 'PENDING' ? (
                    <div className={styles.statusBadge} data-cy={`${index}-bry-signer-status`}>
                      <FormattedMessage id={`signatures.status.${signer.status}`} />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <div className={styles.tableFooter}>
          <button 
            onClick={add} 
            className={classNames(styles.plusButton, 'rounded-full flex items-center justify-center')}
            data-cy="add-bry-signer"
            type="button"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

BrySignersField.propTypes = {
  formData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      id: PropTypes.number,
      status: PropTypes.string,
      typeMessaging: PropTypes.arrayOf(PropTypes.string),
      phone: PropTypes.string,
      personal_identifier: PropTypes.string,
      personal_identifier_type: PropTypes.string,
      authentications: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  onChange: PropTypes.func,
  errorSchema: PropTypes.array,
};

BrySignersField.defaultProps = {
  formData: null,
  onChange: () => {},
  errorSchema: [],
};

export default BrySignersField;
